import React, { useRef, useState } from "react";
import { MarketStatsMarket, MarketStatsStat } from "../MarketStats/MarketStats";
import Heading from "./Heading";
import Slider, { Settings } from "react-slick";
import { BackgroundImage } from "../Media";
import { Button } from "../Button";

export interface MarketStatCollection {
    label: string;
    markets: MarketStatsMarket[];
}
export interface IMarketStatsWithSliderBladProps {
    title?: string;
    titleHeadingLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";
    titleClassNames?: string;
    sectionClassNames?: string;
    marketsCollection: MarketStatCollection[];
    marketStatsLength?: number;
    statsBgImage?: string;
    marketClassNames?: string;
    activeMarketClassNames?: string;
    collectionClassNames?: string;
    activeCollectionClassNames?: string;
    statCardClassNames?: string;
    contentColText?: string;
    contentColClassNames?: string;
    contentColCTA?: { href: string; text?: string; target?: string },
    contentColCTAClassNames?: string;
    bottomCTADefaultText?: string
    bottomCTAClassNames?: string;
    sliderSettings?: Partial<Settings>
}

export const MarketStatsWithSliderBlade: React.FC<IMarketStatsWithSliderBladProps> = ({
    title,
    titleClassNames = "",
    titleHeadingLevel = "h2",
    sectionClassNames = "",
    marketsCollection,
    statsBgImage,
    collectionClassNames = "",
    activeCollectionClassNames = "",
    marketClassNames = "",
    activeMarketClassNames = "",
    statCardClassNames = "",
    contentColText,
    contentColClassNames = "",
    contentColCTA,
    contentColCTAClassNames = "",
    bottomCTAClassNames = "",
    bottomCTADefaultText,
    sliderSettings,
    marketStatsLength = 6,
}) => {

    let sliderRef = useRef<any>(null);

    const MarketStatsWrapper = () => {
        const wrapperDefaultClassNames = `mt-7 w-full max-w-[1440px] mx-auto flex flex-wrap`
        const childComponent = (
            <>
                <div className={`flex flex-col items-start gap-y-2 justify-between p-7 w-full lg:w-2/5 order-last lg:order-first ${contentColClassNames}`}>
                    <div dangerouslySetInnerHTML={{ __html: contentColText ?? "" }}></div>
                    {contentColCTA && (
                        <Button children={contentColCTA.text} linkProps={{ className: contentColCTAClassNames, href: contentColCTA.href, target: contentColCTA.target }}></Button>
                    )}
                </div>
                <div className="w-full lg:w-3/5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                    {marketData.map((item, index) => (
                        <div key={index} className={`flex flex-col items-center justify-center py-8 ${statCardClassNames}`}>
                            <span className={`text-6xl flex items-start`}>
                                <>
                                    {typeof item.value === "string" && item.value.startsWith("$") && (
                                        <span className="text-2xl">$</span>
                                    )}
                                    {typeof item.value === "string" ? item.value.replace("$", "") : item.value}
                                </>
                            </span>
                            <span>{item.label}</span>
                        </div>
                    ))}
                    {marketData.length < marketStatsLength && [...Array(marketStatsLength - marketData.length)].map((item, index) => (
                        <div className={`hidden md:flex flex-col items-center justify-center py-8 ${statCardClassNames}`} key={index}></div>
                    ))}
                </div>
            </>
        )

        return (
            <>
                {statsBgImage && (
                    <BackgroundImage
                        className={`${wrapperDefaultClassNames}`}
                        src={statsBgImage}
                    >
                        {childComponent}
                    </BackgroundImage>
                )}
                {!statsBgImage && (
                    <div
                        className={`${wrapperDefaultClassNames}`}
                    >
                        {childComponent}
                    </div>
                )}
            </>
        )
    }

    const collectionLabels: string[] = [];
    marketsCollection?.forEach((item, index) => {
        collectionLabels.push(item.label)
    })

    const [selectedCollection, setSelectedCollection] = useState(0);
    const _marketLabels = marketsCollection[0].markets?.map(item => {
        return item.marketLabel;
    });
    const [marketLabels, setMarketlabels] = useState(_marketLabels);
    const [selectedMarketLabel, setSelectedMarketLabel] = useState(0);
    const _marketData = marketsCollection[0].markets?.[0].marketData;
    const [marketData, setMarketData] = useState<MarketStatsStat[]>(_marketData)
    const onCollectionClick = (index: number) => {
        setSelectedCollection(index)
        const _newMarketLabels = marketsCollection[index].markets?.map(item => {
            return item.marketLabel;
        });
        setMarketlabels(_newMarketLabels)
        setSelectedMarketLabel(0)
        setMarketData(marketsCollection[index].markets[0].marketData)
        sliderRef.current?.slickGoTo(0)
    }
    const onMarketLabelClick = (index: number) => {
        setSelectedMarketLabel(index)
        setMarketData(marketsCollection[selectedCollection].markets[index].marketData)
        sliderRef.current?.slickGoTo(index)
    }

    return (
        <section className={`w-full relative py-14 px-2  ${sectionClassNames}`}>
            {title && (
                <Heading className={`mb-10 w-full mx-auto max-w-[1440px] text-inherit text-5xl ${titleClassNames}`} headingLevel={titleHeadingLevel}>{title}</Heading>
            )}
            {collectionLabels?.[0] &&
                <div className="mb-4 w-full mx-auto max-w-[1440px] flex flex-wrap justify-center lg:justify-start divide-x">
                    {collectionLabels.map((item, index) => (
                        <span key={index} className={`px-6`}>
                            <a onClick={() => { onCollectionClick(index) }} className={`cursor-pointer ${selectedCollection === index ? `border-b border-accent ${activeCollectionClassNames}` : `${collectionClassNames}`}`}>{item}</a>
                        </span>
                    ))}
                </div>
            }
            {marketLabels?.[0] &&
                <Slider
                    ref={sliderRef}
                    className="center mx-auto w-full max-w-[90%] 2xl:max-w-[1290px] [&_.slick-track]:gap-2.5 [&_.slick-track]:items-center"
                    centerMode={true}
                    slidesToShow={5}
                    slidesToScroll={1}
                    initialSlide={0}
                    infinite={true}
                    centerPadding="0px"
                    beforeChange={(currentSlide: number, nextSlide: number) => {
                        setSelectedMarketLabel(nextSlide)
                        setMarketData(marketsCollection[selectedCollection].markets[nextSlide].marketData)
                    }}
                    responsive={[
                        {
                            breakpoint: 1279,
                            settings: {
                                slidesToShow: 3
                            }
                        },
                        {
                            breakpoint: 767,
                            settings: {
                                slidesToShow: 1
                            }
                        }
                    ]}
                    {...sliderSettings}
                >
                    {marketLabels.map((item, index) => (
                        <button onClick={() => { onMarketLabelClick(index) }} key={index} className={`text-center py-7 uppercase ${selectedMarketLabel === index ? `py-9 ${activeMarketClassNames}` : `py-7 ${marketClassNames}`}`}>
                            {item}
                        </button>
                    ))}
                </Slider>
            }
            <MarketStatsWrapper />
            {marketsCollection[selectedCollection].markets[selectedMarketLabel].marketUrl &&
                <div className="flex justify-center">
                    <Button linkProps={{
                        href: marketsCollection[selectedCollection].markets[selectedMarketLabel].marketUrl,
                        className: `mx-auto mt-14 ${bottomCTAClassNames}`
                    }}>
                        {bottomCTADefaultText}
                    </Button>
                </div>
            }
        </section>
    )
}

export default MarketStatsWithSliderBlade